const DEFAULT_PAGE_SIZE = 20;

const state = {
    items: [],
    projects: [],
    total_count: 0,
    current_count: 0,
    page: 1,
    itemsPerPage: DEFAULT_PAGE_SIZE,
    page_count: 0,
    search: '',
    regex: false,
    current_project_id: null,
    panel_open: false,
    auto_opened: false,
    open_asset_id: null,
    loading: false,
};

const mutations = {
    set_inbox(state, payload) {
        state.items = payload.items || [];
        state.projects = payload.projects || [];
        state.total_count = payload.total_count || 0;
        state.current_count = payload.current_count || 0;
        state.page = payload.page || 1;
        state.itemsPerPage = payload.itemsPerPage || DEFAULT_PAGE_SIZE;
        state.page_count = payload.page_count || 0;
        if (payload.search !== undefined) {
            state.search = payload.search || '';
        }
        if (payload.regex !== undefined) {
            state.regex = !!payload.regex;
        }
        state.current_project_id = payload.current_project_id;
    },
    set_search_options(state, { search, regex, page, itemsPerPage }) {
        if (search !== undefined) {
            state.search = search;
        }
        if (regex !== undefined) {
            state.regex = !!regex;
        }
        if (page !== undefined) {
            state.page = page;
        }
        if (itemsPerPage !== undefined) {
            state.itemsPerPage = itemsPerPage;
        }
    },
    set_loading(state, value) {
        state.loading = value;
    },
    set_panel_open(state, value) {
        state.panel_open = value;
    },
    set_auto_opened(state, value) {
        state.auto_opened = value;
    },
    set_open_asset_id(state, asset_id) {
        state.open_asset_id = asset_id;
    },
    remove_message(state, message_id) {
        state.items = state.items.filter((m) => m.id !== message_id);
        if (state.total_count > 0) {
            state.total_count -= 1;
        }
        if (state.current_count > 0) {
            state.current_count -= 1;
        }
    },
    reset(state) {
        state.items = [];
        state.projects = [];
        state.total_count = 0;
        state.current_count = 0;
        state.page = 1;
        state.itemsPerPage = DEFAULT_PAGE_SIZE;
        state.page_count = 0;
        state.search = '';
        state.regex = false;
        state.panel_open = false;
        state.auto_opened = false;
        state.open_asset_id = null;
    },
};

const actions = {
    async fetch_inbox(context, payload = {}) {
        const project_id =
            payload.project_id || sessionStorage.getItem('project_id') || null;
        const page = payload.page != null ? payload.page : context.state.page;
        const itemsPerPage =
            payload.itemsPerPage != null
                ? payload.itemsPerPage
                : context.state.itemsPerPage;
        const search =
            payload.search !== undefined ? payload.search : context.state.search;
        const regex =
            payload.regex !== undefined ? payload.regex : context.state.regex;

        context.commit('set_search_options', {
            search,
            regex,
            page,
            itemsPerPage,
        });
        context.commit('set_loading', true);
        try {
            const response = await this._vm.$api.messages.inbox(project_id, {
                page,
                itemsPerPage,
                search,
                regex,
            });
            if (!response) {
                return;
            }
            context.commit('set_inbox', {
                items: response.messages,
                projects: response.projects,
                total_count: response.total_count,
                current_count: response.current_count,
                page: response.page,
                itemsPerPage: response.itemsPerPage,
                page_count: response.page_count,
                search: response.search,
                regex: response.regex,
                current_project_id: response.current_project_id,
            });
            if (
                payload.auto_open &&
                response.total_count > 0 &&
                !context.state.auto_opened
            ) {
                context.commit('set_panel_open', true);
                context.commit('set_auto_opened', true);
            }
        } finally {
            context.commit('set_loading', false);
        }
    },
    toggle_panel(context) {
        const next = !context.state.panel_open;
        context.commit('set_panel_open', next);
        if (next) {
            context.dispatch('fetch_inbox', { page: context.state.page });
        }
    },
    open_asset(context, asset_id) {
        context.commit('set_open_asset_id', asset_id);
    },
    close_asset(context) {
        context.commit('set_open_asset_id', null);
    },
    async archive(context, { asset_id, message_id }) {
        const res = await this._vm.$api.asset.archiveAssetMessage(
            asset_id,
            message_id,
        );
        if (res && res.error) {
            context.dispatch('snackbars/add_failed_snack', String(res.error), {
                root: true,
            });
            return;
        }
        context.commit('remove_message', message_id);
        // Refetch so the page fills from later items when a slot frees up.
        if (context.state.items.length === 0 && context.state.page > 1) {
            await context.dispatch('fetch_inbox', {
                page: context.state.page - 1,
            });
        } else {
            await context.dispatch('fetch_inbox', {});
        }
    },
};

const getters = {
    get_items(state) {
        return state.items;
    },
    get_total_count(state) {
        return state.total_count;
    },
    get_current_count(state) {
        return state.current_count;
    },
    get_page(state) {
        return state.page;
    },
    get_page_count(state) {
        return state.page_count;
    },
    get_search(state) {
        return state.search;
    },
    get_regex(state) {
        return state.regex;
    },
    is_panel_open(state) {
        return state.panel_open;
    },
    get_open_asset_id(state) {
        return state.open_asset_id;
    },
    other_projects(state) {
        return state.projects.filter(
            (p) => String(p.project_id) !== String(state.current_project_id),
        );
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
